import React, { useState } from 'react';
import { addDays, eachDayOfInterval, endOfMonth, format, startOfMonth, getDay } from 'date-fns';
import SubNav from '../components/subNav';
import SEO from '../components/SEO';
import { baseUrl } from '../config';



const initialTimelineData = [
  { task: 'Script Breakdowns', days: 5, start: 3, color: 'bg-red-600' },
  { task: 'Finalize Script', days: 3, start: 1, color: 'bg-pink-500' },
  { task: 'Location Scouting', days: 5, start: 5, color: 'bg-gray-500' },
  { task: 'Shot Lists', days: 6, start: 10, color: 'bg-yellow-600' },
  { task: 'Shooting Schedule', days: 4, start: 15, color: 'bg-green-500' },
  { task: 'Casting', days: 3, start: 19, color: 'bg-blue-700' },
  { task: 'Rehearsals', days: 2, start: 22, color: 'bg-teal-600' },
  { task: 'Shooting', days: 3, start: 24, color: 'bg-purple-600' },
  { task: 'Post-production', days: 4, start: 27, color: 'bg-orange-600' },
];

const colorOptions = [
  'bg-red-600', 'bg-pink-500', 'bg-gray-500', 'bg-yellow-600', 
  'bg-green-500', 'bg-blue-700', 'bg-teal-600', 'bg-purple-600'
];

const Calendar = () => {
  const [selectedYear, setSelectedYear] = useState(2024);
  const [selectedMonth, setSelectedMonth] = useState(8);
  const [timelineData, setTimelineData] = useState(initialTimelineData);
  const [showForm, setShowForm] = useState(false);
  const [taskDetails, setTaskDetails] = useState({
    task: '',
    startDate: '',
    days: '',
    color: colorOptions[0],
  });

  const startDate = startOfMonth(new Date(selectedYear, selectedMonth));
  const endDate = endOfMonth(startDate);
  const dates = eachDayOfInterval({ start: startDate, end: endDate });
  
  // Calculate calendar grid
  const firstDayOfMonth = getDay(startDate);
  const totalDays = dates.length;
  const totalWeeks = Math.ceil((totalDays + firstDayOfMonth) / 7);
  
  const getDayTasks = (date) => {
    const dayOfMonth = parseInt(format(date, 'd'));
    return timelineData.filter(task => {
      const taskStart = task.start;
      const taskEnd = task.start + task.days - 1;
      return dayOfMonth >= taskStart && dayOfMonth <= taskEnd;
    });
  };

  const handleYearChange = (e) => setSelectedYear(Number(e.target.value));
  const handleMonthChange = (e) => setSelectedMonth(Number(e.target.value));

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTaskDetails({ ...taskDetails, [name]: value });
  };

  const handleAddTask = (e) => {
    e.preventDefault();

    const startDateObject = new Date(taskDetails.startDate);
    const startOfMonthDate = startOfMonth(new Date(selectedYear, selectedMonth));
    const startDay = Math.ceil((startDateObject - startOfMonthDate) / (1000 * 60 * 60 * 24)) + 1;

    if (startDay < 1 || startDay > endDate.getDate()) {
      alert('Task start date must be within the selected month.');
      return;
    }

    const newTask = {
      ...taskDetails,
      task: taskDetails.task,
      start: startDay,
      days: parseInt(taskDetails.days, 10),
      color: taskDetails.color,
    };

    setTimelineData([...timelineData, newTask]);
    setShowForm(false);
    setTaskDetails({
      task: '',
      startDate: '',
      days: '',
      color: colorOptions[0],
    });
  };

  return (
    <div className="p-4 h-screen">
      <SEO
        title="Production Calendar"
        description="Plan and manage your film production schedule with ProducerBuddy's interactive production calendar tool."
        url="https://producerbuddy.pilomation.com/calender"
        robots="noindex, nofollow"
      />
      <SubNav className="w-full" />
      <div className="flex justify-center p-3 mb-4 space-x-4">
        <select value={selectedYear} onChange={handleYearChange} className="p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500">
          {[2023, 2024, 2025, 2026].map((year) => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
        <select value={selectedMonth} onChange={handleMonthChange} className="p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500">
          {[
            'January', 'February', 'March', 'April', 'May', 'June', 'July',
            'August', 'September', 'October', 'November', 'December'
          ].map((month, index) => (
            <option key={index} value={index}>{month}</option>
          ))}
        </select>
        <button onClick={() => setShowForm(!showForm)} className="p-2 bg-blue-600 text-white rounded">
          {showForm ? 'Cancel' : 'Add Task'}
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="border rounded-lg shadow">
        <div className="grid grid-cols-7 gap-0 bg-gray-100 font-semibold border-b">
          {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day) => (
            <div key={day} className="p-2 text-center border-r last:border-r-0">
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-0">
          {Array.from({ length: totalWeeks * 7 }).map((_, index) => {
            const currentDate = addDays(startDate, index - firstDayOfMonth);
            const isCurrentMonth = format(currentDate, 'M') === format(startDate, 'M');
            const dayTasks = isCurrentMonth ? getDayTasks(currentDate) : [];

            return (
              <div
                key={index}
                className={`min-h-24 p-1 border-r border-b relative ${
                  isCurrentMonth ? 'bg-white' : 'bg-gray-50'
                }`}
              >
                <div className="text-right p-1">
                  <span className={`${isCurrentMonth ? 'text-gray-900' : 'text-gray-400'}`}>
                    {format(currentDate, 'd')}
                  </span>
                </div>
                <div className="space-y-1">
                  {dayTasks.map((task, idx) => (
                    <div
                      key={idx}
                      className={`${task.color} text-white text-xs p-1 rounded truncate`}
                      title={task.task}
                    >
                      {task.task}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Add Task Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-1/3">
            <h2 className="text-lg font-bold mb-4">Add New Task</h2>
            <form onSubmit={handleAddTask}>
              <div className="flex flex-col space-y-2">
                <input
                  type="text"
                  name="task"
                  value={taskDetails.task}
                  onChange={handleInputChange}
                  className="p-2 border rounded"
                  placeholder="Task name"
                  required
                />
                <input
                  type="date"
                  name="startDate"
                  value={taskDetails.startDate}
                  onChange={handleInputChange}
                  className="p-2 border rounded"
                  required
                />
                <input
                  type="number"
                  name="days"
                  value={taskDetails.days}
                  onChange={handleInputChange}
                  placeholder="Number of Days"
                  className="p-2 border rounded"
                  min="1"
                  required
                />
                <select
                  name="color"
                  value={taskDetails.color}
                  onChange={handleInputChange}
                  className="p-2 border rounded"
                >
                  {colorOptions.map((color) => (
                    <option key={color} value={color}>
                      {color}
                    </option>
                  ))}
                </select>
                <button type="submit" className="p-2 bg-green-600 text-white rounded">
                  Add Task
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="p-2 bg-red-600 text-white rounded"
                >
                  Close
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendar;
